import { useState, useEffect, useMemo } from 'react';
import { css, mediaQueries } from '@eti/styles';
import isFunction from 'lodash/isFunction';
import { m as motion, AnimatePresence } from 'framer-motion';
import { analytics, usePrevious, useScrollTo } from '@eti/utils';
import { useTranslation, useProperty } from '@eti/providers';
import { useDidUpdateEffect } from '../../../custom-hooks/useDidUpdateEffect';
import { SEAT_MAP } from '../../../products';
import { getAvailableDeckOptions } from '../../utils/decks';
import { findIndexOfSegment, findNextAvailableSegment, findPreviousAvailableSegment, isFirstAvailableSegment, isLastAvailableSegment, getBestPriceCharacteristics, getNonSelectedAvailableSegments, getSegment } from '../../utils/segments';
import { getTotalSelectedSeatsForTravelers } from '../../utils/currentSeatSelection';
import { getIdOfFirstUnselectedTraveler, getSegmentTravelers, getNextTravelerId, getPreviousTravelerId, getNonBookedTravelerSeats } from '../../utils/travelers';
import Summary from '../../summary/components/SummaryTable';
import { SEATING_PREFERENCE_OPTIONS, CONFIRMATION_MODAL_REDIRECT_TO, CONFIRMATION_MODAL_ACTION_TYPE, CONFIRMATION_MODAL_TYPE } from '../../utils/constants';
import { flatBounds } from '../../utils/bounds';
import { transformSeatmap } from '../utils/getGraphQlSelection';
import SeatMapHeader from './SeatMapHeader';
import MainContent from './SeatMapMainContent';
import Footer from './SeatMapFooter';
import SeatMapConfirmation from './SeatMapConfirmation';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const seatMapWrapperStyles = backDrop => css`
  background-color: #fff;
  border-radius: 0.1875rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  @media ${mediaQueries.small.only} {
    ${backDrop && css`
      &::before {
        background-color: rgb(0 0 0 / 40%);
        border-radius: 0.1875rem;
        content: '';
        inset: 0;
        overflow: hidden;
        position: absolute;
        z-index: 4;
      }
    `}
  }
`;
const headerMainContentStyles = legendHeight => css`
  margin-bottom: max(43px, ${legendHeight}px);
`;
const motionVariants = {
  enter: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};
const SeatMapContent = _ref => {
  let {
    callback = () => {},
    getSelected = () => [],
    modifySeatMapUserSelectionCallback = () => [],
    onChange = () => {},
    onOptOut,
    onRemove = () => {},
    onSeatConfirmation,
    seatingBounds = [],
    selectedSeats,
    totalMarkup,
    totalPrice,
    travelers
  } = _ref;
  const {
    p
  } = useProperty();
  const {
    t
  } = useTranslation();
  const lowerDeckLabel = t('Product.SeatMap.DeckSelection.LowerDeck');
  const mainDeckLabel = t('Product.SeatMap.DeckSelection.MainDeck');
  const upperDeckLabel = t('Product.SeatMap.DeckSelection.UpperDeck');
  const flattenedBounds = useMemo(() => flatBounds(seatingBounds), [seatingBounds]);
  const {
    scrollVertical
  } = useScrollTo();
  const scrollToTop = () => scrollVertical('smooth', 0);
  const firstAvailableSegment = flattenedBounds[0];
  const getActiveDeck = options => {
    const mainDeck = options.find(deck => deck.value === 'mainDeck');
    return mainDeck || options[0];
  };
  const getAvailableDeckOptionsForSegment = index => {
    const currentSegment = flattenedBounds.find(_ref2 => {
      let {
        boundSegmentIndex
      } = _ref2;
      return boundSegmentIndex === index;
    });
    return getAvailableDeckOptions(currentSegment, upperDeckLabel, mainDeckLabel, lowerDeckLabel);
  };
  const firstAvailableSegmentActiveDeck = getActiveDeck(getAvailableDeckOptionsForSegment(firstAvailableSegment.boundSegmentIndex));
  const getInitialActiveTravelerId = travelerIds => (travelerIds || [])[0] || 0;
  const [activeDeck, setActiveDeck] = useState(firstAvailableSegmentActiveDeck);
  const [activeSegment, setActiveSegment] = useState(firstAvailableSegment.boundSegmentIndex);
  const [activeTraveler, setActiveTraveler] = useState(getInitialActiveTravelerId(firstAvailableSegment.travelerIds));
  const [isSummaryShowing, setIsSummaryShowing] = useState(false);
  const [fromSummary, setFromSummary] = useState(false);
  const [direction, setDirection] = useState(0);
  const [legendHeight, setLegendHeight] = useState(0);
  const [activeBoundIndex, activeSegmentIndex] = activeSegment.split('-').map(Number);
  const previousActiveSegment = usePrevious(activeSegment);
  const [backDrop, setBackDrop] = useState(false);
  const [showAllSeatsSelectedConfirmation, setShowAllSeatsSelectedConfirmation] = useState();
  const [confirmationModalRedirectTo, setConfirmationModalRedirectTo] = useState(CONFIRMATION_MODAL_REDIRECT_TO.DEFAULT);
  const [confirmationModalType, setConfirmationModalType] = useState(CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS);
  const isManualNavigationEnabled = p('IbeClient.SeatMap.Segment.Navigation.Manual.Enabled');
  const bestPriceCharacteristics = getBestPriceCharacteristics(flattenedBounds, activeSegment);
  const isFirstSegment = isFirstAvailableSegment(flattenedBounds, activeSegment);
  const totalSelectedSeats = getTotalSelectedSeatsForTravelers(selectedSeats);
  const nonSelectedAvailableSegments = getNonSelectedAvailableSegments(flattenedBounds, totalSelectedSeats);
  const allSeatsSelected = (nonSelectedAvailableSegments || []).length === 0;
  const handleBackDrop = open => {
    setBackDrop(open);
  };
  useEffect(() => {
    const isDifferentSegment = previousActiveSegment && previousActiveSegment !== activeSegment;
    const getDirection = (boundIndex, previousBoundIndex, segmentIndex, previousSegmentIndex) => {
      if (boundIndex >= previousBoundIndex) {
        if (boundIndex === previousBoundIndex) {
          if (segmentIndex > previousSegmentIndex) {
            return 1;
          }
          if (segmentIndex < previousSegmentIndex) {
            return -1;
          }
          return 0;
        }
        return 1;
      }
      return -1;
    };
    if (isDifferentSegment) {
      const [previousBoundIndex, previousSegmentIndex] = previousActiveSegment.split('-').map(Number);
      const dir = getDirection(activeBoundIndex, previousBoundIndex, activeSegmentIndex, previousSegmentIndex);
      setDirection(dir);
    }
  }, [activeBoundIndex, activeSegment, activeSegmentIndex, direction, previousActiveSegment]);
  const handleChangeSegment = segment => {
    const {
      boundSegmentIndex,
      travelerIds
    } = segment;
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'SEGMENTS'
    }, {
      event: 'seatingVisible',
      seatingCurrentSegment: findIndexOfSegment(flattenedBounds, boundSegmentIndex) + 1
    });
    const deckOptions = getAvailableDeckOptionsForSegment(boundSegmentIndex);
    setActiveDeck(getActiveDeck(deckOptions));
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler(getInitialActiveTravelerId(travelerIds));
    setIsSummaryShowing(false);
    setFromSummary(false);
  };
  const onModifySeatMapUserSelectionCallback = () => {
    if (isFunction(modifySeatMapUserSelectionCallback)) {
      var _selectedSeats$value, _seatSelection$select;
      const seatSelection = transformSeatmap(String(SEAT_MAP.id), {
        selected: (selectedSeats === null || selectedSeats === void 0 || (_selectedSeats$value = selectedSeats.value) === null || _selectedSeats$value === void 0 ? void 0 : _selectedSeats$value.value) === 'true',
        ...selectedSeats
      });
      modifySeatMapUserSelectionCallback((_seatSelection$select = seatSelection === null || seatSelection === void 0 ? void 0 : seatSelection.selectionSeatMap) !== null && _seatSelection$select !== void 0 ? _seatSelection$select : []);
    }
  };
  const handleNextSegmentClick = () => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'CONTINUE'
    });
    if (isSummaryShowing) {
      return;
    }
    const isLastAvailableSegmentActive = isLastAvailableSegment(flattenedBounds, activeSegment);
    if (isLastAvailableSegmentActive) {
      if (isManualNavigationEnabled && !allSeatsSelected) {
        setConfirmationModalType(CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS);
        setConfirmationModalRedirectTo(CONFIRMATION_MODAL_REDIRECT_TO.SUMMARY);
        setShowAllSeatsSelectedConfirmation(true);
      } else {
        setIsSummaryShowing(true);
        setFromSummary(false);
      }
    } else {
      const segment = findNextAvailableSegment(flattenedBounds, activeSegment);
      if (segment) {
        handleChangeSegment(segment);
      }
    }
  };
  const getCurrentSegmentHeader = endpoint => {
    var _boundName$endpoint$c, _boundName$endpoint;
    const {
      boundName = {}
    } = flattenedBounds.find(_ref3 => {
      let {
        boundSegmentIndex
      } = _ref3;
      return boundSegmentIndex === activeSegment;
    });
    return (_boundName$endpoint$c = (_boundName$endpoint = boundName[endpoint]) === null || _boundName$endpoint === void 0 ? void 0 : _boundName$endpoint.city) !== null && _boundName$endpoint$c !== void 0 ? _boundName$endpoint$c : '';
  };
  const handleChangeSeat = _ref4 => {
    let {
      carrier,
      column,
      price,
      row,
      seat,
      markup,
      seatPreference
    } = _ref4;
    return () => {
      const newActiveSegment = getSelected(activeSegment);
      if (newActiveSegment.some(s => s.value === seat) && !seatPreference) {
        if (isManualNavigationEnabled && selectedSeats) {
          var _newActiveSegment$fin;
          setActiveTraveler((_newActiveSegment$fin = newActiveSegment.find(s => s.value === seat)) === null || _newActiveSegment$fin === void 0 ? void 0 : _newActiveSegment$fin.id);
        }
        return;
      }
      onChange(activeSegment, activeTraveler, carrier, column, row, seat, price, markup, seatPreference, activeDeck === null || activeDeck === void 0 ? void 0 : activeDeck.value);
    };
  };
  const handleChangeTraveler = traveler => () => {
    setActiveTraveler(traveler);
    if (Number(traveler) !== 0) {
      setDirection(0);
    }
    if (fromSummary) {
      setFromSummary(false);
    }
  };
  const handleOnSeatConfirmation = data => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'BOOK'
    }, {
      event: 'seatingVisible',
      seatingOnBook: 'YES'
    });
    onModifySeatMapUserSelectionCallback();
    if (isFunction(onSeatConfirmation)) {
      onSeatConfirmation(data);
    }

    // TODO: AN-2261 - Add flow tests for force enabled seat map modal
    if (isFunction(callback)) {
      callback(selectedSeats);
    }
  };
  const handleDeckChange = option => {
    setActiveDeck(option);
  };
  const handleOnOptOut = async () => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'NO'
    });
    if (isFunction(onOptOut)) {
      await onOptOut();
    }
    if (isFunction(callback)) {
      callback();
    }
    onModifySeatMapUserSelectionCallback();
  };
  const handleRemoveSeat = (segment, traveler) => () => {
    onRemove(segment, traveler, selectedSeats);
  };
  const handleToggleSeatSelect = (boundSegmentIndex, traveler) => () => {
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler(traveler);
    const deckOptions = getAvailableDeckOptionsForSegment(boundSegmentIndex);
    setActiveDeck(getActiveDeck(deckOptions));
    setIsSummaryShowing(false);
    setFromSummary(true);
    scrollToTop();
  };
  const setActiveNextTraveler = () => {
    if (flattenedBounds.length === 0) {
      return;
    }
    const nextTravelerId = getNextTravelerId(getSegmentTravelers(travelers, flattenedBounds, activeSegment), activeTraveler);
    if (nextTravelerId !== null) {
      handleChangeTraveler(nextTravelerId)();
    }
  };
  const handlePreviousSegmentClick = () => {
    const isFirstAvailableSegmentActive = isFirstAvailableSegment(flattenedBounds, activeSegment);
    if (isFirstAvailableSegmentActive) {
      return;
    }
    if (isSummaryShowing) {
      setConfirmationModalRedirectTo(CONFIRMATION_MODAL_REDIRECT_TO.DEFAULT);
      setIsSummaryShowing(false);
      setFromSummary(false);
    } else {
      const segment = findPreviousAvailableSegment(flattenedBounds, activeSegment);
      if (segment) {
        handleChangeSegment(segment);
      }
    }
  };
  const setActivePreviousTraveler = () => {
    if (flattenedBounds.length === 0) {
      return;
    }
    const previousTravelerId = getPreviousTravelerId(getSegmentTravelers(travelers, flattenedBounds, activeSegment), activeTraveler);
    if (previousTravelerId !== null) {
      handleChangeTraveler(previousTravelerId)();
    }
  };
  useEffect(() => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingCurrentSegment: activeSegment
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const prevSelectedSeatsInSegmentCount = getSelected(activeSegment).length;
  useDidUpdateEffect(() => {
    if (flattenedBounds.length !== 0) {
      const {
        travelerIds = [],
        __typename: segmentType
      } = flattenedBounds.find(segment => segment.boundSegmentIndex === activeSegment);
      const selectedSeatsInSegmentCount = getSelected(activeSegment).length;
      const travelersAllHaveSeat = selectedSeatsInSegmentCount === travelerIds.length;
      if ((selectedSeatsInSegmentCount !== prevSelectedSeatsInSegmentCount && travelersAllHaveSeat || segmentType === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_PREFERENCE || segmentType === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_BESIDE) && !isManualNavigationEnabled) {
        handleNextSegmentClick();
      }
      const nextUnselectedTraveler = getIdOfFirstUnselectedTraveler(getSegmentTravelers(travelers, flattenedBounds, activeSegment), getSelected(activeSegment), activeTraveler);
      if (nextUnselectedTraveler !== null) {
        handleChangeTraveler(nextUnselectedTraveler)();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeats]);
  analytics.useLogMouseFlowTagEffect('Seatmap 4.0');
  const handleConfirmationSelection = (action, segment) => {
    setShowAllSeatsSelectedConfirmation(false);
    if (action === CONFIRMATION_MODAL_ACTION_TYPE.SEAT_MAP_PERSIST_ACTION_DISMISS) {
      if (confirmationModalRedirectTo === CONFIRMATION_MODAL_REDIRECT_TO.SUMMARY) {
        setIsSummaryShowing(true);
        setFromSummary(false);
      } else {
        handleOnOptOut();
      }
    } else {
      if (!segment) {
        return;
      }
      const nonBookedTravelerSeats = getNonBookedTravelerSeats(flattenedBounds.find(seg => seg.boundSegmentIndex === segment), totalSelectedSeats);
      handleToggleSeatSelect(segment, Number(nonBookedTravelerSeats[0]))();
      setConfirmationModalRedirectTo(CONFIRMATION_MODAL_REDIRECT_TO.DEFAULT);
    }
  };
  const handleSkipButton = () => {
    setConfirmationModalType(CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT);
    setShowAllSeatsSelectedConfirmation(true);
  };
  const isShowingSkipLinkButton = p('Seating.Skip.Seat.Selection.LinkButton');
  return /*#__PURE__*/_jsxs("div", {
    className: seatMapWrapperStyles(backDrop),
    "data-offer-rate": SEAT_MAP.id,
    "data-testid": "seatMapContent-wrapper",
    children: [/*#__PURE__*/_jsxs("div", {
      className: bestPriceCharacteristics && !isSummaryShowing ? headerMainContentStyles(legendHeight) : '',
      children: [/*#__PURE__*/_jsx(SeatMapHeader, {
        active: [activeSegment, activeTraveler],
        currentSegmentData: getSegment(flattenedBounds, activeSegment),
        currentSegmentIndex: findIndexOfSegment(flattenedBounds, activeSegment) + 1,
        from: getCurrentSegmentHeader('from'),
        isShowingSkipLinkButton: isShowingSkipLinkButton,
        isSummaryShowing: isSummaryShowing,
        onChangeTraveler: handleChangeTraveler,
        onNextTravelerClick: setActiveNextTraveler,
        onPreviousTravelerClick: setActivePreviousTraveler,
        onRemoveSeat: handleRemoveSeat,
        onSkip: handleSkipButton,
        segments: flattenedBounds,
        selectedSeats: getSelected(activeSegment),
        to: getCurrentSegmentHeader('to'),
        totalSegmentsCount: flattenedBounds.length,
        travelers: getSegmentTravelers(travelers, flattenedBounds, activeSegment)
      }), isSummaryShowing ? /*#__PURE__*/_jsx(Summary, {
        getSelected: getSelected,
        onRemoveSeat: handleRemoveSeat,
        onToggleSeatSelect: handleToggleSeatSelect,
        segments: flattenedBounds,
        totalMarkup: totalMarkup,
        totalPrice: totalPrice,
        travelers: travelers
      }) : /*#__PURE__*/_jsx(MainContent, {
        active: [activeSegment, activeTraveler],
        deckOptions: getAvailableDeckOptionsForSegment(activeSegment),
        direction: direction,
        isFromSummary: fromSummary,
        onChangeSeat: handleChangeSeat,
        onDeckChange: handleDeckChange,
        segments: flattenedBounds,
        selectedDeck: activeDeck,
        selectedSeats: getSelected(activeSegment),
        travelers: getSegmentTravelers(travelers, flattenedBounds, activeSegment)
      })]
    }), /*#__PURE__*/_jsx(Footer, {
      active: [activeSegment, activeTraveler],
      handleBackDrop: handleBackDrop,
      isFirstSegment: isFirstSegment,
      legendHeight: legendHeight,
      onSaveAndContinue: handleNextSegmentClick,
      onSeatConfirmation: handleOnSeatConfirmation,
      onToPreviousSegment: handlePreviousSegmentClick,
      segments: flattenedBounds,
      setLegendHeight: setLegendHeight,
      shouldShowSummary: isSummaryShowing
    }), /*#__PURE__*/_jsx(AnimatePresence, {
      children: showAllSeatsSelectedConfirmation && /*#__PURE__*/_jsx(motion.div, {
        animate: "enter",
        exit: "exit",
        transition: {
          type: 'tween',
          duration: 0.3
        },
        variants: motionVariants,
        children: /*#__PURE__*/_jsx(SeatMapConfirmation, {
          onSelection: handleConfirmationSelection,
          segments: nonSelectedAvailableSegments,
          type: confirmationModalType
        })
      }, "seatMapContent-allSeatsSelectedConfirmation")
    })]
  });
};
export default SeatMapContent;
//# sourceMappingURL=SeatMapContent.js.map